import React, { useState, useEffect } from "react";
import fetch from "../shared/Fetch";
import styled from "styled-components";
import { formatDate } from "../shared/TimeUtil";
import Spinner from "../sharedComponents/Spinner";
import AppHeading from "../sharedComponents/AppHeading";
import { AddNewButton } from "../sharedComponents/Atoms";
import { CSVLink } from "react-csv";
import FileUpload from "../sharedComponents/FileUpload";
import { ReactComponent as AddIcon } from "../svgs/AddIcon.svg";
import NewConsultantForm from "./NewConsultantForm";
import ConsultantRowItem from "./ConsultantRowItem";

const Container = styled.div`
  padding: 2rem 2rem;
  color: var(--color-grey);
  box-sizing: border-box;
`;

const ConsultantTable = styled.table`
  width: 100%;
  table-layout: fixed;

  & th:nth-child(1),
  & td:nth-child(1) {
    width: 18%;
  }
  & th:nth-child(2),
  & td:nth-child(2) {
    width: 18%;
  }
  & th:nth-child(3),
  & td:nth-child(3) {
    width: 13%;
  }
  & th:nth-child(4),
  & td:nth-child(4) {
    width: 17%;
  }
  & th:nth-child(5),
  & td:nth-child(5) {
    width: 13%;
  }
  & th:nth-child(6),
  & td:nth-child(6) {
    width: 7%;
  }
  & th:nth-child(7),
  & td:nth-child(7) {
    width: 7%;
  }
  & th:nth-child(8),
  & td:nth-child(8) {
    width: 3%;
    text-align: center;
    white-space: nowrap;
  }
`;

const Th = styled.th`
  font-size: 1.1rem;
  padding-bottom: 2rem;
  padding-top: 1rem;
  text-align: left;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const AddConsultantIcon = styled(AddIcon)`
  height: 1.1rem;
  width: 1.1rem;
  margin-right: 0.3rem;
  & * {
    fill: var(--color-grey);
  }
`;

const AddConsultantButton = styled(AddNewButton)`
  display: ${props => (props.showButton ? "flex" : "none")};
  align-items: center;
  background: transparent;
  font-weight: bold;
  cursor: pointer;
  margin: 0;
`;

const ToolsButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-lightergrey);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  &:hover {
    color: var(--color-grey);
  }
`;

const ToolsMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
`;

const ToolsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  z-index: 6;
  background: #fcfcfc;
  border: 1px solid var(--color-grey);
  min-width: 14rem;
  display: flex;
  flex-direction: column;
`;

const ToolsMenuChevron = styled.span`
  display: inline-block;
  width: 5px;
  height: 5px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
`;

const ToolsMenuItem = styled.div`
  font-size: 0.85rem;
  color: var(--color-grey);
  padding: 0.6rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  &:hover {
    color: var(--theme-link-color);
    background: var(--group-hover-color);
  }
`;

function Administer({ match }) {
  const {
    departments: departmentsParam,
    groups: groupsParam,
    seniorities: senioritiesParam,
    serviceareas: serviceareasParam,
    weeks,
    offset,
    availableOnly
  } = match.params;

  const [consultants, setConsultants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [seniorities, setSeniorities] = useState([]);
  const [showNewConsultantRow, setShowNewConsultantRow] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [waitingForData, setWaitingForData] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);

  useEffect(() => {
    fetchConsultants();
    fetchDepartments();
    fetchGroups();
    fetchCurrentUser();
    fetchSeniorities();
    fetchAdmins();
  }, []);

  async function fetchCurrentUser() {
    const user = await fetch("/user/current");
    if (user.email === "oms@variant.no") setIsSuperAdmin(true);
  }

  function fetchAdmins() {
    fetch("/admins")
      .then(response => {
        if (Array.isArray(response)) setAdmins(response);
      })
      .catch(() => console.log("Something went wrong while fetching admins"));
  }

  async function fetchConsultants() {
    setWaitingForData(true);
    try {
      const response = await fetch(
        "/consultants?departments=" + departmentsParam
      );
      setConsultants(Array.isArray(response) ? response : []);
    } catch (e) {
      console.log("Something went wrong while fetching Consultants", e);
    } finally {
      setWaitingForData(false);
    }
  }

  async function fetchDepartments() {
    try {
      const response = await fetch("/departments");
      if (Array.isArray(response)) setDepartments(response);
    } catch (e) {
      console.log("Something went wrong while fetching departments", e);
    }
  }

  async function fetchGroups() {
    try {
      const response = await fetch("/groups");
      if (Array.isArray(response)) {
        setGroups(
          response.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase()
              ? -1
              : a.name.toLowerCase() > b.name.toLowerCase()
              ? 1
              : 0
          )
        );
      }
    } catch (e) {
      console.log("Something went wrong while fetching groups", e);
    }
  }

  async function fetchSeniorities() {
    try {
      const response = await fetch("/seniorities");
      if (Array.isArray(response)) setSeniorities(response);
    } catch (e) {
      console.log("Something went wrong while fetching seniorities", e);
    }
  }

  function isAdmin(email) {
    if (!email) return false;
    return admins && admins.indexOf(email) !== -1;
  }

  function toggleAdmin(email, checked) {
    if (!email) return;
    const prev = admins || [];
    if (checked) {
      setAdmins([...new Set([...prev, email])]);
      fetch("/admins", { method: "POST", body: JSON.stringify({ email }) })
        .then(resp => {
          if (resp && resp.error) setAdmins(prev);
        })
        .catch(() => setAdmins(prev));
    } else {
      setAdmins(prev.filter(e => e !== email));
      fetch(`/admins/${encodeURIComponent(email)}`, { method: "DELETE" })
        .then(resp => {
          if (resp && resp.error) setAdmins(prev);
        })
        .catch(() => setAdmins(prev));
    }
  }

  function handleInputChange(index, e) {
    const property = e.target.name;
    const updated = consultants.map((val, i) => {
      if (i !== index) return val;
      let value = e.target.value;
      if (property === "name" || property === "email") value = value.trim();
      return { ...val, [property]: value };
    });
    setConsultants(updated);
    saveConsultant(updated[index]);
  }

  function handleDepartmentSelect(index, departmentId) {
    const updated = consultants.map((c, i) =>
      i === index ? { ...c, departmentId } : c
    );
    setConsultants(updated);
    saveConsultant(updated[index]);
  }

  function handleSenioritySelect(index, seniorityId) {
    const updated = consultants.map((c, i) =>
      i === index ? { ...c, seniorityId } : c
    );
    setConsultants(updated);
    saveConsultant(updated[index]);
  }

  function handleStartDateChange(date, index) {
    setConsultants(
      consultants.map((c, i) =>
        i === index
          ? { ...c, startDate: date ? formatDate(date, "YYYYMMDD") : null }
          : c
      )
    );
  }

  function handleEndDateChange(date, index) {
    setConsultants(
      consultants.map((c, i) =>
        i === index
          ? { ...c, endDate: date ? formatDate(date, "YYYYMMDD") : null }
          : c
      )
    );
  }

  function handleSaveConsultant(index) {
    saveConsultant(consultants[index]);
  }

  function saveConsultant(consultant) {
    const payload = {
      consultantId: consultant.consultantId,
      name: consultant.name,
      email: consultant.email,
      departmentId:
        consultant.departmentId !== ""
          ? consultant.departmentId
          : (departments[0] || {}).id,
      seniorityId:
        consultant.seniorityId !== "" ? consultant.seniorityId : null,
      endDate: consultant.endDate,
      startDate: consultant.startDate
    };
    fetch("/consultants", {
      method: "PUT",
      body: JSON.stringify(payload)
    }).catch(reason =>
      console.log("Something went wrong while performing the request", reason)
    );
  }

  function postConsultantGroupConnection(consultantId, groupId) {
    if (!consultantId || !groupId) return;
    fetch(`/consultants/${consultantId}/group`, {
      method: "POST",
      body: JSON.stringify({ groupId })
    })
      .then(() => fetchConsultants())
      .catch(reason =>
        console.log("Something went wrong during updating group name", reason)
      );
  }

  function removeConsultantGroupConnection(consultantId, groupId) {
    if (!consultantId || !groupId) return;
    fetch(`/consultants/${consultantId}/group`, {
      method: "DELETE",
      body: JSON.stringify({ groupId })
    })
      .then(() => fetchConsultants())
      .catch(reason =>
        console.log("Something went wrong during updating group name", reason)
      );
  }

  function toggleNewConsultant(e) {
    e.preventDefault();
    setShowNewConsultantRow(s => !s);
  }

  function getAllGroupsString(consultant) {
    if (consultant.groups && consultant.groups.length > 0) {
      const names = consultant.groups.map(g => g.name);
      return names.length === 1
        ? names[0]
        : `${names[0]} (+${names.length - 1})`;
    }
    return "Legg til tjeneste";
  }

  function getDepartmentNameById(id) {
    const d = (departments || []).find(dep => dep.id === id);
    return d ? d.name : "";
  }

  function getSeniorityNameById(id) {
    const s = (seniorities || []).find(se => se.id === id);
    return s ? s.name : "";
  }

  async function handleConsultantImport(rows) {
    setWaitingForData(true);
    const res = await fetch("/consultants/import", {
      method: "POST",
      body: JSON.stringify(rows)
    });
    if (res.error.length > 0) {
      setError({
        updateErrors: res.error.map(e => e.name + ": " + e.reason),
        message: `Det ble importert ${res.success.length} konsulenter. ${res.error.length} konsulenter ble ikke importert`
      });
    } else {
      setSuccessMessage(`Det ble importert ${res.success.length} konsulenter`);
    }
    setWaitingForData(false);
    fetchConsultants();
  }

  async function handleProjectImport(rows) {
    setWaitingForData(true);
    const res = await fetch("/engagements/import", {
      method: "POST",
      body: JSON.stringify(rows)
    });
    if (res.error.length > 0) {
      setError({
        updateErrors: res.error.map(e => e.name + ": " + e.reason),
        message: `Det ble importert ${res.success.length} prosjekter. ${res.error.length} prosjekter ble ikke importert`
      });
    } else {
      setSuccessMessage(`Det ble importert ${res.success.length} prosjekter`);
    }
    setWaitingForData(false);
  }

  async function saveNewConsultant(formData) {
    const payload = {
      name: formData.name.trim(),
      email: formData.email ? formData.email.trim() : undefined,
      departmentId: formData.departmentId || (departments[0] || {}).id,
      seniorityId: formData.seniorityId || null,
      startDate: formData.startDate,
      endDate: formData.endDate
    };
    const response = await fetch("/consultants", {
      method: "POST",
      body: JSON.stringify(payload)
    });
    if (response.error) throw new Error(response.error);
    const consultantId = response.generatedId;
    await Promise.all(
      (formData.selectedGroupIds || []).map(groupId =>
        fetch(`/consultants/${consultantId}/group`, {
          method: "POST",
          body: JSON.stringify({ groupId })
        })
      )
    );
    const selectedGroups = (Array.isArray(groups) ? groups : []).filter(g =>
      (formData.selectedGroupIds || []).includes(g.id)
    );
    setShowNewConsultantRow(false);
    setConsultants(prev =>
      prev.concat({ ...payload, consultantId, groups: selectedGroups })
    );
  }

  const linkToNewGroup = `/${departmentsParam}/all/${senioritiesParam}/all/groups/${weeks}/${offset}/false`;

  const csvHeader = [
    { label: "Konsulent", key: "name" },
    { label: "Lokasjon", key: "department" },
    { label: "Epost", key: "email" },
    { label: "Tjeneste", key: "groups" },
    { label: "Stillingsnivå", key: "seniority" },
    { label: "Sluttdato", key: "endDate" }
  ];
  const csvData = consultants.map(d => ({
    name: d.name,
    department: getDepartmentNameById(d.departmentId),
    groups: (d.groups || []).map(g => g.name).join(", ") || undefined,
    seniority: getSeniorityNameById(d.seniorityId || ""),
    endDate: d.endDate,
    email: d.email
  }));

  return (
    <div className="module">
      <AppHeading
        departments={departmentsParam}
        offset={offset}
        weeks={weeks}
        groups={groupsParam}
        availableOnly={availableOnly}
        seniorities={senioritiesParam}
        serviceareas={serviceareasParam}
        active="administer"
      />
      <Container>
        <form className="administer-consultants">
          <Spinner show={waitingForData} />
          <ActionsBar>
            <div style={{ position: "relative" }}>
              <ToolsButton
                type="button"
                onClick={() => setToolsMenuOpen(o => !o)}
              >
                Verktøy <ToolsMenuChevron />
              </ToolsButton>
              {toolsMenuOpen && (
                <>
                  <ToolsMenuBackdrop onClick={() => setToolsMenuOpen(false)} />
                  <ToolsDropdown>
                    <CSVLink
                      filename="Konsulenter.csv"
                      data={csvData}
                      headers={csvHeader}
                      separator=";"
                      onClick={() => setToolsMenuOpen(false)}
                    >
                      <ToolsMenuItem>Last ned CSV</ToolsMenuItem>
                    </CSVLink>
                    {isSuperAdmin && (
                      <>
                        <FileUpload
                          secondary
                          handleRows={handleProjectImport}
                          error={error}
                          successMessage={successMessage}
                        >
                          Batchimport av prosjekter
                        </FileUpload>
                        <FileUpload
                          secondary
                          handleRows={handleConsultantImport}
                          error={error}
                          successMessage={successMessage}
                        >
                          Batchimport av konsulenter
                        </FileUpload>
                      </>
                    )}
                  </ToolsDropdown>
                </>
              )}
            </div>
            <AddConsultantButton
              onClick={toggleNewConsultant}
              type="button"
              showButton={!showNewConsultantRow}
            >
              <AddConsultantIcon />
              Ny konsulent
            </AddConsultantButton>
          </ActionsBar>
          {showNewConsultantRow && (
            <NewConsultantForm
              departments={departments}
              seniorities={seniorities}
              groups={groups}
              onSave={saveNewConsultant}
              onClose={toggleNewConsultant}
            />
          )}
          <ConsultantTable>
            <thead>
              <tr>
                <Th>Navn</Th>
                <Th>E-post</Th>
                <Th>Lokasjon</Th>
                <Th>Tjeneste</Th>
                <Th>Stillingsnivå</Th>
                <Th>Startdato</Th>
                <Th>Sluttdato</Th>
                <Th>Admin</Th>
              </tr>
            </thead>
            <tbody>
              {consultants.map((consultant, i) => (
                <ConsultantRowItem
                  key={consultant.consultantId}
                  consultant={consultant}
                  index={i}
                  isAdmin={isAdmin}
                  toggleAdmin={toggleAdmin}
                  handleInputChange={handleInputChange}
                  departments={departments}
                  groups={groups}
                  seniorities={seniorities}
                  getDepartmentNameById={getDepartmentNameById}
                  handleDepartmentSelect={handleDepartmentSelect}
                  getAllGroupsString={getAllGroupsString}
                  postConsultantGroupConnection={postConsultantGroupConnection}
                  removeConsultantGroupConnection={
                    removeConsultantGroupConnection
                  }
                  getSeniorityNameById={getSeniorityNameById}
                  handleSenioritySelect={handleSenioritySelect}
                  handleStartDateChange={handleStartDateChange}
                  handleEndDateChange={handleEndDateChange}
                  handleSaveConsultant={handleSaveConsultant}
                  linkToNewGroup={linkToNewGroup}
                />
              ))}
            </tbody>
          </ConsultantTable>
          {consultants.length > 15 && (
            <ActionsBar>
              <span />
              <AddConsultantButton
                onClick={toggleNewConsultant}
                type="button"
                showButton={!showNewConsultantRow}
              >
                <AddConsultantIcon />
                Ny konsulent
              </AddConsultantButton>
            </ActionsBar>
          )}
        </form>
      </Container>
    </div>
  );
}

export default Administer;
